import { keyBy } from 'lodash';
import { useCallback, useMemo } from 'react';

import { Bank, Order } from 'types';

type UseBankUtilsInput = {
  banks: Bank[] | undefined;
  order: Order | undefined;
};

export type UseBankUtilsResult = {
  getBankNameByCode: (code?: string) => string;
  requisitesBankName: string;
  customerBankName: string;
  customerBankCode: string;
};

export const useBankUtils = ({
  banks,
  order,
}: UseBankUtilsInput): UseBankUtilsResult => {
  const banksByCode = useMemo(() => keyBy(banks, (bank) => bank.code), [banks]);

  const getBankNameByCode = useCallback(
    (code?: string) => (code ? banksByCode[code]?.name || code : ''),
    [banksByCode],
  );

  const requisitesBankName = useMemo(
    () =>
      order?.requisites?.bankName || getBankNameByCode(order?.requisites?.bank),
    [getBankNameByCode, order?.requisites?.bank, order?.requisites?.bankName],
  );

  const customerBankName = useMemo(
    () => getBankNameByCode(order?.payment?.customerBank),
    [getBankNameByCode, order?.payment?.customerBank],
  );

  const customerBankCode = useMemo(() => {
    const raw = order?.payment?.customerBank;
    if (!raw) {
      return '';
    }
    const found = banks?.find((b) => b.code === raw || b.name === raw);
    return found?.code ?? raw;
  }, [banks, order?.payment?.customerBank]);

  return {
    getBankNameByCode,
    requisitesBankName,
    customerBankName,
    customerBankCode,
  };
};
